import { useMemo } from 'react';
import {
  getTimeMarkers,
  TimeMarkerCellRenderer,
  TimeMarkerCellRendererValue,
} from '@higo/ui';
import { TableOptions } from 'react-table';
import {
  DiagnosedByCellRenderer,
  DiagnosedByCellValue,
  StatusCellRenderer,
} from 'components/TableRenderers';
import { ExaminationPatientDTOPatientType } from '@higo/api/lib/models';
import {
  ExaminationDTOStatus,
  ExaminationType,
  PatientExaminations,
  PatientVisit,
} from '@higo/api';
import { DateCellRenderer } from 'components/PatientExaminationsTable/renderers/DateCellRenderer';
import { ExamTypeCellRenderer } from 'components/PatientExaminationsTable/renderers/ExamTypeCellRenderer';
import { TableSortDesc } from '@higo/ui/lib/components/Table';
import { useIntl } from 'react-intl';

export type PatientExamsListItem = {
  id: number;
  timeMarker: TimeMarkerCellRendererValue;
  examination: {
    createDate: Date;
    type: ExaminationType | 'VISIT';
    doctorId?: number;
    status: ExaminationDTOStatus | string;
  };
  patient?: {
    type: ExaminationPatientDTOPatientType;
  };
  diagnosedBy?: DiagnosedByCellValue;
  isVisit: boolean;
};

export const usePatientExamsConfig = () => {
  const intl = useIntl();

  return useMemo(
    (): Omit<TableOptions<PatientExamsListItem>, 'data'> => ({
      columns: [
        {
          accessor: 'timeMarker',
          width: 70,
          isMarker: true,
          Cell: TimeMarkerCellRenderer,
        },
        {
          Header: intl.formatMessage({ id: 'table.header.examDate' }),
          accessor: 'examination.createDate',
          width: 120,
          Cell: DateCellRenderer,
        },
        {
          Header: intl.formatMessage({ id: 'common.type' }),
          accessor: 'examination.type',
          width: 180,
          Cell: ExamTypeCellRenderer,
        },
        {
          Header: intl.formatMessage({ id: 'table.header.diagnosedBy' }),
          accessor: 'diagnosedBy',
          width: 150,
          Cell: DiagnosedByCellRenderer,
        },
        {
          Header: intl.formatMessage({ id: 'table.header.status' }),
          accessor: 'examination.status',
          Cell: StatusCellRenderer,
        },
      ],
    }),
    [intl],
  );
};

export const usePatientExamsConfigForMobile = () => {
  const intl = useIntl();

  return useMemo(
    (): Omit<TableOptions<PatientExamsListItem>, 'data'> => ({
      columns: [
        {
          Header: intl.formatMessage({ id: 'table.header.examDate' }),
          accessor: 'examination.createDate',
          width: 90,
          Cell: DateCellRenderer,
        },
        {
          Header: intl.formatMessage({ id: 'table.header.diagnosedBy' }),
          accessor: 'diagnosedBy',
          width: 140,
          Cell: DiagnosedByCellRenderer,
        },
        {
          Header: intl.formatMessage({ id: 'table.header.status' }),
          accessor: 'examination.status',
          Cell: StatusCellRenderer,
          width: 140,
        },
      ],
    }),
    [intl],
  );
};

export const mapToPatientExamsList = (
  exams: PatientExaminations[],
): PatientExamsListItem[] => {
  const timeMarkers = getTimeMarkers({
    timestamps: exams.map((el) => el.createDate),
    sortDirection: TableSortDesc, // backend should return ordered by create/session end date in descending order
  });

  return exams.map((exam, index) => ({
    id: exam.examinationId,
    timeMarker: timeMarkers[index],
    examination: {
      createDate: exam.createDate,
      type: exam.type,
      doctorId: exam.doctor?.id,
      status: exam.status,
    },
    patient: {
      type: exam.patientType,
    },
    diagnosedBy: exam.doctor,
    isVisit: false,
  }));
};

// Merge examinations and visits into one list, sorted by createDate descending
export const mapToPatientHistoryList = (
  exams: PatientExaminations[],
  visits: PatientVisit[],
): PatientExamsListItem[] => {
  const examsItems: Omit<PatientExamsListItem, 'timeMarker'>[] = exams.map((exam) => ({
    id: exam.examinationId,
    examination: {
      createDate: exam.createDate,
      type: exam.type,
      doctorId: exam.doctor?.id,
      status: exam.status,
    },
    patient: {
      type: exam.patientType,
    },
    diagnosedBy: exam.doctor,
    isVisit: false,
  }));
  const visitsItems: Omit<PatientExamsListItem, 'timeMarker'>[] = visits.map((visit) => ({
    id: visit.id,
    examination: {
      createDate: visit.createDate,
      type: 'VISIT' as const,
      doctorId: visit.executedBy?.id || undefined,
      status: visit.status,
    },
    diagnosedBy: visit.executedBy
      ? {
          id: visit.executedBy.id || 0,
          firstName: visit.executedBy.firstName,
          lastName: visit.executedBy.lastName,
        }
      : undefined,
    isVisit: true,
  }));
  const allItems = [...examsItems, ...visitsItems].sort(
    (a, b) => b.examination.createDate.getTime() - a.examination.createDate.getTime(),
  );

  const timeMarkers = getTimeMarkers({
    timestamps: allItems.map((item) => item.examination.createDate),
    sortDirection: TableSortDesc,
  });

  return allItems.map((item, index) => ({
    ...item,
    timeMarker: timeMarkers[index],
  }));
};
